import React, { memo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import Chip, { ChipSizes, ChipTypes } from '@lib/components/Chip/Chip';
import Image from '@lib/components/Image/Image';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import Tooltip from '@lib/components/Tooltip/Tooltip';
import Typography from '@lib/components/Typography/Typography';
import { formatBytes } from '@lib/utils/files';
import { AttachmentValue } from '@lib/components/FileUploaderUppy/FileUploaderUppy';
import { hasTextEditorValue } from '@lib/components/TextEditor/utils';
import { DocumentationPreviewEnum } from './enums';
import DocumentVersionNotes from './DocumentVersionNotes';
import getAttachmentMeta from './utils/getAttachmentMeta';
import getPreviewImgUrl from './utils/getPreviewImgUrl';
import styles from './DocumentationPreview.module.scss';

type Props = {
  actions?: React.ReactNode;
  customName?: string | null;
  attachment: AttachmentValue | null | undefined;
  isFavorite?: boolean;
  isRequired?: boolean;
  isUnread?: boolean;
  // Rendered directly before the name text (e.g. an AiFieldAssist trigger
  // while the name is still being AI-generated) — deliberately a plain
  // ReactNode rather than anything AI-specific, since this component is
  // shared far beyond Documentation's AI-detection feature. Wrapped in a
  // click-stopping span so interacting with it doesn't also fire `onClick`.
  namePrefix?: React.ReactNode;
  notValidated?: boolean;
  notes?: string;
  onClick?: () => void;
  useDefaultPdfPreview?: boolean;
  view?: DocumentationPreviewEnum;
};

function DocumentationPreview(props: Props) {
  const {
    actions,
    customName,
    attachment,
    isFavorite,
    isRequired,
    isUnread,
    namePrefix,
    notValidated,
    notes,
    onClick,
    useDefaultPdfPreview,
    view = DocumentationPreviewEnum.RowCard,
  } = props;
  const { t } = useTranslation();
  const [isNotesOpen, setIsNotesOpen] = useState(false);

  if (!attachment) return null;
  const { mimeType, size, filename, fileFormat } = getAttachmentMeta(
    attachment,
    customName,
  );

  const isDocumentRowCard = view === DocumentationPreviewEnum.DocumentRowCard;
  const isBlockCard = view === DocumentationPreviewEnum.BlockCard;

  const imgUrl = getPreviewImgUrl({
    attachment,
    mimeType,
    fileFormat,
    view,
    useDefaultPdfPreview,
  });

  const previewContent = (
    /* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */
    <div
      className={classNames(styles.documentationPreview, {
        [styles.documentationPreviewLink]: onClick,
      })}
      onClick={onClick}
    >
      <div className={styles.documentationPreviewImage}>
        <Image src={imgUrl} />
        {isBlockCard && (
          <svg viewBox="0 0 1 1" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="1" height="1" />
          </svg>
        )}
      </div>
      <div className={styles.documentationPreviewInfoWrap}>
        <div className={styles.documentationPreviewNameRow}>
          {notValidated && (
            <span className={styles.documentationPreviewNamePrefix}>
              <Tooltip body={t('not-validated-document-tooltip')}>
                <MaterialIcon
                  cursor="help"
                  icon="warning"
                  filled
                  size="md-16"
                  className={styles.notValidatedIcon}
                />
              </Tooltip>
            </span>
          )}
          {isFavorite && (
            <span className={styles.documentationPreviewNamePrefix}>
              <MaterialIcon
                icon="bookmark_star"
                symbolsOutlined
                filled
                size="md-16"
              />
            </span>
          )}
          {namePrefix && (
            /* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */
            <span
              className={styles.documentationPreviewNamePrefix}
              onClick={(e) => e.stopPropagation()}
            >
              {namePrefix}
            </span>
          )}
          <Typography
            variant="body"
            className={styles.documentationPreviewName}
            strong={isDocumentRowCard}
          >
            {filename}
          </Typography>
          {isUnread && (
            <span className={styles.documentationPreviewNameSuffix}>
              <Chip
                type={ChipTypes.chipRed}
                label={t('unread')}
                size={ChipSizes.extraSmall}
              />
            </span>
          )}
          {isRequired && (
            <span className={styles.documentationPreviewNameSuffix}>
              <Chip
                type={ChipTypes.chipRed}
                label={t('required')}
                size={ChipSizes.extraSmall}
              />
            </span>
          )}
        </div>
        <div className={styles.documentationPreviewInfo}>
          {mimeType && (
            <Typography variant="caption">
              {fileFormat.toUpperCase()}
            </Typography>
          )}
          {mimeType && size && (
            <span className={styles.documentationPreviewInfoSeparator} />
          )}
          {size && (
            <Typography variant="caption">{formatBytes(size)}</Typography>
          )}
          {isDocumentRowCard && hasTextEditorValue(notes) && (
            <>
              <span className={styles.documentationPreviewInfoSeparator} />
              {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
              <span
                className={styles.versionNotesLink}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsNotesOpen(true);
                }}
              >
                {t('version-notes')}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={classNames({
        [styles.rowCard]: !isDocumentRowCard && !isBlockCard,
        [styles.blockCard]: isBlockCard,
        [styles.documentRowCard]: isDocumentRowCard,
      })}
    >
      {isDocumentRowCard ? (
        <div className={styles.documentRowCardWrap}>
          {previewContent}
          {actions}
        </div>
      ) : (
        previewContent
      )}
      {isDocumentRowCard && hasTextEditorValue(notes) && (
        <DocumentVersionNotes
          isOpen={isNotesOpen}
          notes={notes}
          onClose={() => setIsNotesOpen(false)}
        />
      )}
    </div>
  );
}

export default memo(DocumentationPreview);
